'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { report } from '~/lib/exception-handler';

// Booking-funnel error boundary (event detail → tickets → checkout). Without
// this segment-level boundary an uncaught crash anywhere in the funnel fell
// through to the root error.tsx — a context-free "Something went wrong / Go
// home" that dropped the user out of the funnel entirely. Here we keep them
// one click from the event catalog and tag the report with the segment so
// funnel crashes are distinguishable in AppSignal.
export default function EventFunnelError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    report(error, {
      filename: 'app/events/[slug]/error.tsx',
      method: 'EventFunnelError',
      tags: {
        segment: 'events/[slug]',
        ...(error.digest ? { digest: error.digest } : {}),
      },
    });
  }, [error]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
      <h1 className="text-2xl font-semibold text-gray-900">
        Something went wrong with this event
      </h1>
      <p className="text-sm text-gray-600">
        We&apos;ve been notified. You can try again, or head back to browse
        other events — any booking you already completed is safe.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
        >
          Try again
        </button>
        <Link
          href="/events"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to events
        </Link>
      </div>
    </div>
  );
}
